import React, { useRef, useEffect } from 'react';
import { XIcon, BotIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AiMessageBubble } from './AiMessageBubble';
import { AiChatInput } from './AiChatInput';
import { useAiChat } from '../../hooks/useAiChat';

export const AiChatWindow: React.FC = () => {
  const { isOpen, closeChat, messages, isTyping, sendMessage, isInitializing, clearChatHistory, agentName, personaName, greetingMessage } = useAiChat();
  const { t } = useTranslation('aiAgents');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, isTyping]);

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-[144px] right-6 w-[400px] max-w-[calc(100vw-48px)] max-h-[600px] h-[calc(100vh-160px)] flex flex-col bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-50 transition-all duration-300 ease-in-out">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg">
            <BotIcon size={20} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{agentName || 'Ring2All Assistant'}</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">{personaName || 'Context-Aware AI Helper'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearChatHistory}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title="Clear Chat History"
          >
            <Trash2Icon size={18} />
          </button>
          <button
            onClick={closeChat}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Close Chat"
          >
            <XIcon size={20} />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
        {messages.length === 0 && !isInitializing && (
          <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 opacity-70 p-6">
            <BotIcon size={48} className="mb-4 text-slate-300 dark:text-slate-600" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {greetingMessage || t('chat.defaultGreeting') || "Hello! I'm your PBX Assistant."}
            </p>
            <p className="text-xs mt-2 text-slate-500 dark:text-slate-400">
              {t('chat.defaultGreetingSub') || "Ask me anything about the platform configuration."}
            </p>
          </div>
        )}

        {isInitializing && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-primary-500">
            <Loader2Icon size={24} className="animate-spin mb-2" />
            <span className="text-xs">Loading history...</span>
          </div>
        )}

        {messages.map((msg) => (
          <AiMessageBubble key={msg.id} message={msg} />
        ))}

        {isTyping && (
          <div className="flex w-full justify-start mb-4">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-none px-4 py-3 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <AiChatInput onSendMessage={sendMessage} isTyping={isTyping} />
    </div>
  );
};


